import { useEffect, useState, type ReactNode } from 'react';
import { getAuthPassword, setAuthPassword, clearAuthPassword } from '../lib/local.js';
import { setUnauthorizedHandler } from '../lib/api.js';

/**
 * Gerbang password — dipasang di root App.tsx, bungkus seluruh Routes.
 * Private single-user tool (PRD §20.3): satu shared password, bukan sistem
 * user/session. Password disimpan di localStorage supaya tidak perlu login
 * ulang tiap buka app (lihat catatan di lib/local.ts).
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [password, setPasswordState] = useState<string | null>(() => getAuthPassword());
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kalau request mana pun kena 401 (password berubah di server, atau salah),
  // paksa balik ke layar login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuthPassword();
      setPasswordState(null);
      setError('Sesi berakhir, masukkan password lagi.');
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim()) return;
    setChecking(true);
    setError(null);

    try {
      // Endpoint ringan mana pun di bawah /api/bff/* cukup untuk verifikasi —
      // pakai settings/providers karena responnya kecil.
      const res = await fetch('/api/bff/settings/providers', {
        headers: { 'X-Auth-Password': input },
      });

      if (res.status === 401) {
        setError('Password salah.');
        setChecking(false);
        return;
      }

      setAuthPassword(input);
      setPasswordState(input);
    } catch {
      setError('Gagal menghubungi server, coba lagi.');
    } finally {
      setChecking(false);
    }
  }

  if (password) return <>{children}</>;

  return (
    <div className="auth-gate">
      <form className="auth-gate__card" onSubmit={handleSubmit}>
        <h1 style={{ fontSize: 20, marginBottom: 4 }}>Studio</h1>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Masukkan password untuk lanjut.
        </p>
        <input
          type="password"
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Password"
          className="auth-gate__input"
        />
        {error && (
          <p style={{ color: 'var(--status-error)', fontSize: 13, marginTop: 8 }}>{error}</p>
        )}
        <button type="submit" className="btn-primary tap-target" disabled={checking} style={{ marginTop: 16, width: '100%' }}>
          {checking ? 'Memeriksa...' : 'Masuk'}
        </button>
      </form>
    </div>
  );
}
